import * as Mock from "../mocks";
import { MockedProvider, MockedResponse } from "@apollo/client/testing";
import { AppDataProvider, BookingData, BookingTool, ModalDialog, NonModalDialogVariant, Step } from "../AppDataProvider/AppDataProvider";
import React from "react";
import { createCache } from "../Apollo";
import { StaticMockLink } from "./StaticMockLink";
type CreateTemplateArgs = {
  bookingData?: BookingData;
  bookingTool?: BookingTool;
  currentStep?: Step;
  iFrameId?: string;
  mocks?: MockedResponse<Record<string, unknown>>[];
  modalDialog?: ModalDialog;
  nonModalDialog?: NonModalDialogVariant;
  showReservationExpiry?: boolean;
  userLocation?: string;
};

/**
 * This function creates a storybook template for a component using a provided
 * array of mocks, and optional bookingTool config data.
 *
 * @param Component The react function component you're creating a story for.
 * @param args An object containing:
 *    - bookingTool (optional): booking tool config data.
 *      If no bookingTool is provided, Mocks.defaultBookingTool is used.
 *    - bookingData (optional): data for prefilling the AppDataProvider
 *      with pre-selected data such as customer details.
 *    - currentStep (optional): a Step of the booking flow to skip ahead to.
 *    - mocks (optional): an array of mocks
 *    - modalDialog (optional): config for a modal dialog to be displayed
 *    - nonModalDialog (optional): a non-modal dialog variant to be displayed
 *    - showReservationExpiry (optional): whether the reservation expiry popup
 *      should be shown
 *    - userLocation (optional): a string representing a user location

 *
 * @returns A story with the component wrapped in a MockedProvider using the
 * provided mocks, and an AppDataProvider using the appropriate bookingTool.
 *
 * @example
 * export const Default = createTemplate(AvailabilityStep, {
 *   mocks: [
 *     Mock.defaultAvailabilityIntervalsRequest,
 *     Mock.defaultCreateReservationRequest,
 *   ],
 * }).bind({});
 */
export function createTemplate(Component: React.FunctionComponent, args: CreateTemplateArgs) {
  const {
    mocks = [],
    bookingTool,
    bookingData,
    currentStep,
    modalDialog,
    nonModalDialog,
    showReservationExpiry,
    userLocation,
    iFrameId
  } = args;
  const link = new StaticMockLink(mocks, true);
  const cache = createCache();
  function Template() {
    return <MockedProvider link={link} cache={cache} data-sentry-element="MockedProvider" data-sentry-component="Template" data-sentry-source-file="createTemplate.tsx">
        <AppDataProvider bookingTool={bookingTool ?? Mock.defaultBookingTool} bookingData={bookingData} currentStep={currentStep} modalDialog={modalDialog} nonModalDialog={nonModalDialog} showReservationExpiry={showReservationExpiry} userLocation={userLocation} iFrameId={iFrameId} data-sentry-element="AppDataProvider" data-sentry-source-file="createTemplate.tsx">
          <Component data-sentry-element="Component" data-sentry-source-file="createTemplate.tsx" />
        </AppDataProvider>
      </MockedProvider>;
  }
  return Template;
}

/**
 * This function creates a storybook template for a component which requires
 * props, using a provided array of mocks, and optional bookingTool config data.
 *
 * @param Component The react function component you're creating a story for.
 * @param props The props to be passed to the component being rendered.
 * @param args An object containing:
 *    - bookingTool (optional): booking tool config data.
 *      If no bookingTool is provided, Mocks.defaultBookingTool is used.
 *    - bookingData (optional): data for prefilling the AppDataProvider
 *      with pre-selected data such as customer details.
 *    - currentStep (optional): a Step of the booking flow to skip ahead to.
 *    - mocks: an array of mocks
 *    - modalDialog (optional): config for a modal dialog to be displayed
 *    - nonModalDialog (optional): a non-modal dialog variant to be displayed
 *    - showReservationExpiry (optional): whether the reservation expiry popup
 *      should be shown
 *    - userLocation (optional): a string representing a user location
 *
 * @returns A story with the component wrapped in a MockedProvider using the
 * provided mocks, and an AppDataProvider using the appropriate bookingTool.
 *
 * @example
 * export const Default = createTemplateWithProps<ResourceListProps>(
 *   ResourceList,
 *   { resources: Mock.multipleResources },
 *   { mocks: [] }
 * ).bind({});
 */
export function createTemplateWithProps<PropsType extends Record<string, unknown>>(Component: React.FunctionComponent<PropsType>, props: PropsType, args: CreateTemplateArgs) {
  const component = () => <Component {...props} data-sentry-element="Component" data-sentry-component="component" data-sentry-source-file="createTemplate.tsx" />;
  return createTemplate(component, args);
}
export default createTemplate;